import React, {
  FC, memo, MouseEvent, SVGProps, useMemo,
} from 'react';
import classNames from 'classnames';
import { ReactComponent as SpinnerIcon } from '../../../../assets/icons/spinner.svg';
import { ReactComponent as ArrowBoldIcon } from '../../../../assets/icons/arrowBold.svg';
import styles from './Button.module.scss';

export enum ButtonStyleType {
  primary = 1,
  secondary,
  grey,
  outlined,
  linkButton,
  link,
}

export enum ButtonSizeType {
  sm = 1,
  md,
  lg,
}

export enum ButtonArrowType {
  left = 1,
  right,
}

interface ButtonProps {
  text?: string;
  href?: string;
  onClick?: (event?: MouseEvent) => void;
  styleType?: ButtonStyleType,
  size?: ButtonSizeType,
  arrowType?: ButtonArrowType,
  isInProcess?: boolean;
  isDisabled?: boolean;
  className?: string;
  style?: React.CSSProperties;
  IconComponent?: FC<SVGProps<SVGSVGElement>>;
  dataName?: string;
  additionalAttributes?: {
    [key: string]: string;
  };
}

const buttonStyleClassNames = {
  [ButtonStyleType.primary]: styles.primary,
  [ButtonStyleType.secondary]: styles.secondary,
  [ButtonStyleType.grey]: styles.grey,
  [ButtonStyleType.outlined]: styles.outlined,
  [ButtonStyleType.linkButton]: styles.linkButton,
  [ButtonStyleType.link]: styles.link,
};

const buttonSizeClassNames = {
  [ButtonSizeType.sm]: styles.small,
  [ButtonSizeType.md]: styles.medium,
  [ButtonSizeType.lg]: styles.large,
};

const arrowClassNames = {
  [ButtonArrowType.left]: styles.left,
  [ButtonArrowType.right]: styles.right,
};

const buttonWithArrowClassNames = {
  [ButtonArrowType.left]: styles.withLeftArrow,
  [ButtonArrowType.right]: styles.withRightArrow,
};

const Button: FC<ButtonProps> = ({
  text,
  href,
  onClick,
  styleType = ButtonStyleType.secondary,
  size = ButtonSizeType.lg,
  arrowType,
  isInProcess,
  isDisabled,
  className,
  style,
  IconComponent,
  dataName,
  additionalAttributes,
}: ButtonProps) => {
  const buttonClassName = classNames(styles.button,
    buttonStyleClassNames[styleType],
    buttonSizeClassNames[size],
    !text && styles.withoutText,
    (arrowType && !!text) && buttonWithArrowClassNames[arrowType],
    isInProcess && styles.isInProcess,
    isDisabled && styles.isDisabled,
    className);

  const content = useMemo(() => {
    if (isInProcess) {
      return (
        <SpinnerIcon className={styles.spinnerIcon} />
      );
    }

    const iconWithText = (
      <>
        {IconComponent && <IconComponent className={styles.customIcon} />}
        {text}
      </>
    );

    if (!arrowType) {
      return iconWithText;
    }

    return (
      <>
        {iconWithText}
        <ArrowBoldIcon className={classNames(styles.arrowIcon, arrowClassNames[arrowType])} />
      </>
    );
  }, [
    isInProcess, text, arrowType, IconComponent,
  ]);

  if (href) {
    return (
      <a
        className={buttonClassName}
        href={href}
        {...(dataName && { 'data-name': dataName })}
        {...additionalAttributes}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      className={buttonClassName}
      disabled={isDisabled || isInProcess}
      onClick={onClick}
      type="button"
      style={style}
      {...(dataName && { 'data-name': dataName })}
      {...additionalAttributes}
    >
      {content}
    </button>
  );
};
Button.displayName = 'Button';

export default memo(Button);
